import { useVerifyAuthenticatorSetupMutation } from '@codezo/graphql';
import { useThemeColors } from '@codezo/theme';
import { formatDateTime } from '@codezo/ui';
import {
	ActionIcon,
	Alert,
	Box,
	Button,
	Code,
	CopyButton,
	Group,
	Image,
	Paper,
	PinInput,
	Stack,
	Stepper,
	Text,
	Title,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertCircle, IconCheck, IconCopy } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Logo } from '../components/common/Logo';
import { handleLoginValidate } from '../utils/login';

export function MFASetupPage() {
	const [active, setActive] = useState(0);
	const [setupKey] = useState(''); // In a real app, this would be extracted from the QR code URL
	const [verificationCode, setVerificationCode] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [backupCodes, setBackupCodes] = useState<string[]>([]);
	const [setupComplete, setSetupComplete] = useState(false);
	const [authToken, setAuthToken] = useState<string>('');
	const [userRole, setUserRole] = useState<string>('');
	const [hasDownloaded, setHasDownloaded] = useState(false);
	const [showDownloadWarning, setShowDownloadWarning] = useState(false);
	const location = useLocation();

	const { qrCodeDataURL, userId } = location.state || {};

	const colors = useThemeColors();
	const isMobile = useMediaQuery('(max-width: 48em)');
	// Mutation for verifying the authenticator setup
	const [verifyAuthenticatorSetup, { loading }] = useVerifyAuthenticatorSetupMutation({
		onCompleted: (data) => {
			if (data.verifyAuthenticatorSetup.token && data.verifyAuthenticatorSetup.backupCodes) {
				// Store backup codes and auth info in state
				setBackupCodes(data.verifyAuthenticatorSetup.backupCodes);
				setAuthToken(data.verifyAuthenticatorSetup.token);
				setUserRole(data.verifyAuthenticatorSetup.role);
				setSetupComplete(true);
				// Stay on the same step
			}
		},
		onError: (err) => {
			setError(err.message || 'Invalid verification code. Please try again.');
		},
	});

	const handleVerify = async () => {
		if (!userId || verificationCode.length !== 6) return;

		setError(null);

		try {
			await verifyAuthenticatorSetup({
				variables: {
					input: {
						userId,
						token: verificationCode,
					},
				},
			});
		} catch {
			// Error is handled by onError callback
			// Error handled by mutation onError
		}
	};

	const handleDownloadBackupCodes = () => {
		const content = `Two-Factor Authentication Backup Codes\n\nSave these codes in a secure place. Each code can only be used once.\n\n${backupCodes.join('\n')}\n\nGenerated: ${formatDateTime(new Date())}`;
		const blob = new Blob([content], { type: 'text/plain' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'backup-codes.txt';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
		setHasDownloaded(true);
	};

	const handleContinueAfterBackup = () => {
		if (!hasDownloaded) {
			// Show warning if they haven&apos;t downloaded
			setShowDownloadWarning(true);
			// Auto-hide warning after 3 seconds
			setTimeout(() => setShowDownloadWarning(false), 3000);
			return;
		}

		if (authToken && userRole) {
			// Redirect to the appropriate app
			handleLoginValidate(authToken, userRole);
		}
	};

	return (
		<Box style={{ position: 'relative' }}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
			>
				<Paper
					p={{ base: 'xs', sm: 'xl' }}
					radius="md"
					miw={{ base: 'calc(100vw - 20px)', xs: 600 }}
				>
					<Stack>
						<Stack gap="sm" ta="center">
							<Logo height={40} />
							<div>
								<Title mb={4}>Set Up Two-Factor Authentication</Title>
								<Text size="sm" c={colors.textMuted}>
									Add an extra layer of security to your account
								</Text>
							</div>
						</Stack>

						<Stepper
							active={active}
							size="xs"
							allowNextStepsSelect={false}
							orientation={isMobile ? 'vertical' : 'horizontal'}
							wrap={false}
						>
							<Stepper.Step label="Install App" description="Get authenticator app">
								<Box
									style={{
										display: 'flex',
										flexDirection: 'column',
									}}
									mih={{ base: '250px', xs: '400px' }}
								>
									<Stack gap="md" mt={isMobile ? 'md' : 'xl'} style={{ flex: 1 }}>
										<Text size="sm" c="dimmed">
											Install an authenticator app on your mobile device:
										</Text>
										<Stack gap="xs">
											<Group>
												<Text size="sm">• Google Authenticator</Text>
											</Group>
											<Group>
												<Text size="sm">• Microsoft Authenticator</Text>
											</Group>
											<Group>
												<Text size="sm">• Authy</Text>
											</Group>
											<Group>
												<Text size="sm">• 1Password</Text>
											</Group>
										</Stack>
									</Stack>
									<Button fullWidth size="sm" onClick={() => setActive(1)}>
										Next: Scan QR Code
									</Button>
								</Box>
							</Stepper.Step>

							<Stepper.Step label="Scan QR Code" description="Link your device">
								<Box
									style={{
										display: 'flex',
										flexDirection: 'column',
									}}
									mih={{ base: '350px', xs: '400px' }}
								>
									<Stack
										gap="md"
										mt={isMobile ? 'md' : 'xl'}
										align="center"
										style={{ flex: 1 }}
									>
										<Text size="sm" c={colors.textMuted} ta="center">
											Scan this QR code with your authenticator app:
										</Text>

										<Box
											p="md"
											style={{
												backgroundColor: 'white',
												borderRadius: 8,
											}}
										>
											<Image
												src={qrCodeDataURL || ''}
												width={200}
												height={200}
												alt="MFA QR Code"
											/>
										</Box>

										{setupKey && (
											<>
												<Text size="sm" c={colors.textMuted} ta="center">
													Can&apos;t scan? Enter this code manually:
												</Text>

												<Group>
													<Code>{setupKey}</Code>
													<CopyButton value={setupKey}>
														{({ copied, copy }) => (
															<ActionIcon
																color={copied ? 'green' : 'gray'}
																onClick={copy}
																size="lg"
																variant="subtle"
															>
																{copied ? (
																	<IconCheck size={20} />
																) : (
																	<IconCopy size={20} />
																)}
															</ActionIcon>
														)}
													</CopyButton>
												</Group>
											</>
										)}
									</Stack>
									<Button fullWidth onClick={() => setActive(2)} size="sm">
										Next: Verify Setup
									</Button>
								</Box>
							</Stepper.Step>

							<Stepper.Step label="Verify" description="Confirm setup">
								<Box
									style={{
										display: 'flex',
										flexDirection: 'column',
									}}
									mih={{ base: '250px', xs: '400px' }}
								>
									<Stack
										gap="md"
										mt={isMobile ? 'md' : 'xl'}
										align="center"
										style={{ flex: 1 }}
									>
										{!setupComplete ? (
											<>
												<Text size="sm" c={colors.textMuted} ta="center">
													Enter the 6-digit code from your authenticator
													app
												</Text>

												{error && (
													<Alert
														icon={
															<IconAlertCircle
																size={20}
																color={colors.error}
															/>
														}
														color="error"
														style={{ width: '100%' }}
													>
														{error}
													</Alert>
												)}

												<PinInput
													length={6}
													type="number"
													size={isMobile ? 'md' : 'xl'}
													value={verificationCode}
													onChange={setVerificationCode}
													disabled={loading}
													onComplete={handleVerify}
													error={!!error}
												/>

												{loading && (
													<Text size="sm" c={colors.textMuted}>
														Verifying...
													</Text>
												)}
											</>
										) : (
											<>
												<Alert
													icon={<IconCheck size={20} />}
													color="green"
													style={{ width: '100%' }}
												>
													Two-factor authentication has been successfully
													enabled!
												</Alert>

												<Text size="sm" c={colors.textMuted} ta="center">
													Save your backup codes before continuing.
													You&apos;ll need them if you lose access to your
													authenticator.
												</Text>

												{showDownloadWarning && (
													<Alert
														icon={<IconAlertCircle size={16} />}
														color="red"
														style={{ width: '100%' }}
													>
														You must download your backup codes first!
														You won&apos;t be able to access them later.
													</Alert>
												)}
											</>
										)}
									</Stack>

									{!setupComplete ? (
										<Button
											fullWidth
											onClick={handleVerify}
											size="sm"
											loading={loading}
											disabled={verificationCode.length !== 6}
										>
											Verify and Complete Setup
										</Button>
									) : (
										<Stack gap="xs">
											<Button
												fullWidth
												onClick={handleDownloadBackupCodes}
												size="sm"
												variant={hasDownloaded ? 'light' : 'filled'}
												leftSection={
													hasDownloaded ? <IconCheck size={16} /> : null
												}
											>
												{hasDownloaded
													? 'Backup Codes Downloaded'
													: 'Download Backup Codes'}
											</Button>
											<Button
												fullWidth
												onClick={handleContinueAfterBackup}
												size="sm"
												variant={!hasDownloaded ? 'subtle' : 'filled'}
												color={showDownloadWarning ? 'red' : undefined}
											>
												Continue to Dashboard
											</Button>
										</Stack>
									)}
								</Box>
							</Stepper.Step>
						</Stepper>
					</Stack>
				</Paper>
			</motion.div>
		</Box>
	);
}
